'use client';

import { FormField } from '@/features/forms/FormPrimitives';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Image } from 'react-bootstrap';

export default function HubLfgJoinPanel({
  post,
  currentUser,
  gameName,
  messages = {},
}) {
  const [joinStatus, setJoinStatus] = useState(
    currentUser ? 'loading' : 'none',
  );
  const [requestMessage, setRequestMessage] = useState('');
  const [joinInfo, setJoinInfo] = useState(null);
  const [joinInfoLoading, setJoinInfoLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOwner = Boolean(
    currentUser?.userNumber &&
    post?.userNumber &&
    currentUser.userNumber === post.userNumber,
  );
  const msg = (path, fallback = '') => readHubMessage(messages, path, fallback);
  const loginHref = `/login?redirect=${encodeURIComponent(`/hub/looking-for-game/${post.lfgNumber}`)}`;

  useEffect(() => {
    let isMounted = true;

    const loadJoinState = async () => {
      if (!currentUser || isOwner) {
        setJoinStatus('none');
        return;
      }

      try {
        const response = await clientFetch(
          `/api/hub/lfg/${post.lfgNumber}/join`,
        );

        if (!isMounted) {
          return;
        }

        setJoinStatus(response?.status || 'none');
        setRequestMessage(response?.message || '');
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setJoinStatus('none');
        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'lfgPost.join.errors.loadStatus',
              'Unable to load join status.',
            ),
        );
      }
    };

    loadJoinState();

    return () => {
      isMounted = false;
    };
  }, [currentUser, isOwner, post.lfgNumber]);

  useEffect(() => {
    let isMounted = true;

    const loadJoinInfo = async () => {
      if (!currentUser || joinStatus !== 'approved') {
        setJoinInfo(null);
        return;
      }

      setJoinInfoLoading(true);

      try {
        const response = await clientFetch(
          `/api/hub/lfg/${post.lfgNumber}/join-info`,
        );

        if (!isMounted) {
          return;
        }

        setJoinInfo(response?.joinInfo || null);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'lfgPost.join.errors.loadHostContact',
              'Unable to load host contact.',
            ),
        );
      } finally {
        if (isMounted) {
          setJoinInfoLoading(false);
        }
      }
    };

    loadJoinInfo();

    return () => {
      isMounted = false;
    };
  }, [currentUser, joinStatus, post.lfgNumber]);

  const submitJoinRequest = async () => {
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await clientFetch(
        `/api/hub/lfg/${post.lfgNumber}/join`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            message: requestMessage.trim(),
            contact: currentUser?.email || currentUser?.userNumber || undefined,
          }),
        },
      );

      setJoinStatus(response?.status || 'pending');
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'lfgPost.join.errors.sendRequest',
            'Unable to send join request.',
          ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelJoinRequest = async () => {
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await clientFetch(`/api/hub/lfg/${post.lfgNumber}/join`, {
        method: 'DELETE',
      });

      setJoinStatus('none');
      setJoinInfo(null);
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'lfgPost.join.errors.cancelRequest',
            'Unable to cancel join request.',
          ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="lfg-join-panel hub-action-panel hub-action-panel--lfg">
      <div className="hub-action-panel__header">
        <div>
          <p className="hub-section__eyebrow hub-section__eyebrow--info">
            {msg('lfgPost.join.eyebrow', 'LFG Join')}
          </p>
          <h2 className="join-title">
            {msg('lfgPost.join.sectionTitle', 'Join this party')}
          </h2>
        </div>
        {joinStatus === 'approved' ? (
          <span className="hub-pill hub-pill--accent">
            {msg('lfgPost.join.status.approved', 'Approved')}
          </span>
        ) : joinStatus === 'pending' ? (
          <span className="hub-pill hub-pill--warning">
            {msg('lfgPost.join.status.pending', 'Pending')}
          </span>
        ) : null}
      </div>

      {!currentUser ? (
        <div className="hub-inline-stack">
          <p className="shop-panel__meta">
            {msg(
              'lfgPost.join.loginHint',
              'Log in to request to join and meet the host.',
            )}{' '}
            {gameName ||
              post.gameNumber ||
              msg('lfgPost.join.fallbackGameName', 'this game')}
            .
          </p>
          <div className="account-form__actions">
            <Link
              href={loginHref}
              className="app-button app-button--primary gap-2"
            >
              <span>🔐</span>
              {msg('lfgPost.join.loginCta', 'Log in to join')}
            </Link>
          </div>
        </div>
      ) : isOwner ? (
        <p className="shop-panel__meta">
          {msg(
            'lfgPost.join.ownerHint',
            'You own this post. Review incoming join requests below.',
          )}
        </p>
      ) : post?.isOpen === false ? (
        <p className="shop-panel__meta">
          {msg(
            'lfgPost.join.notJoinable',
            'This post is no longer open for new join requests.',
          )}
        </p>
      ) : joinStatus === 'loading' ? (
        <p className="shop-panel__meta">
          {msg('lfgPost.join.loadingStatus', 'Checking your join status...')}
        </p>
      ) : (
        <div className="hub-inline-stack">
          {joinStatus === 'approved' ? (
            <div className="hub-join-info">
              <p className="shop-panel__meta">
                {msg(
                  'lfgPost.join.approvedHint',
                  'Approved — the host contact is unlocked.',
                )}
              </p>
              {joinInfoLoading ? (
                <p className="shop-panel__meta">
                  {msg(
                    'lfgPost.join.loadingHostContact',
                    'Loading host contact...',
                  )}
                </p>
              ) : null}
              {joinInfo?.contact ? (
                <p>
                  <strong>
                    {msg('lfgPost.join.hostContactLabel', 'Contact')}:
                  </strong>{' '}
                  {joinInfo.contact}
                </p>
              ) : null}
              {joinInfo?.instructions ? (
                <p className="hub-detail__description">
                  {joinInfo.instructions}
                </p>
              ) : null}
            </div>
          ) : joinStatus === 'pending' ? (
            <p className="shop-panel__meta">
              {msg(
                'lfgPost.join.pendingHint',
                'Your request is pending host approval.',
              )}
            </p>
          ) : (
            <>
              <p className="shop-panel__meta">
                {msg(
                  'lfgPost.join.contactAutoHint',
                  "We'll include your account email as your contact.",
                )}
              </p>
              <FormField
                as="textarea"
                label={msg(
                  'lfgPost.join.myMessageLabel',
                  'Message to the host',
                )}
                labelClassName="ms-1"
                rows={4}
                value={requestMessage}
                onChange={(event) => setRequestMessage(event.target.value)}
                placeholder={msg(
                  'lfgPost.join.myMessagePlaceholder',
                  'A quick intro, availability, goals…',
                )}
                inputClassName="ps-3 message-to-host-field"
                controlWrapClassName="mt-1"
              />
            </>
          )}

          {errorMessage ? (
            <div className="auth-message auth-message--error">
              {errorMessage}
            </div>
          ) : null}

          <div className="account-form__actions">
            {joinStatus === 'none' || joinStatus === 'rejected' ? (
              <button
                type="button"
                className="app-button app-button--primary gap-1"
                onClick={submitJoinRequest}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  msg('lfgPost.join.sendingCta', 'Sending...')
                ) : (
                  <>
                    <Image
                      src="/icons/check-warning.png"
                      width={16}
                      height={16}
                      alt=""
                    />{' '}
                    {msg('lfgPost.join.countMeInCta', 'Count Me In')}
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                className="app-button app-button--ghost"
                onClick={cancelJoinRequest}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? msg('lfgPost.join.updatingCta', 'Updating...')
                  : msg('lfgPost.join.cancelCta', 'Cancel request')}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
